import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ShieldCheck, Layers, QrCode, Fingerprint, Sparkles, Headset, ArrowRight } from "lucide-react";
import Seo, { orgLd, localBusinessLd, websiteLd, faqLd } from "../lib/seo";
import { SITE, IMG } from "../lib/site";
import { HOME_TILES, WHY, STEPS, APPLICATIONS, CONFIG_OPTIONS, FAQS, SOLUTIONS, INDUSTRIES, LOCATIONS } from "../data/content";
import { INDUSTRY_ICONS } from "./Industries";
import { Reveal, LineReveal, Overline, ArrowLink } from "../components/site/Reveal";
import QuoteForm from "../components/site/QuoteForm";
import {
  Accordion, AccordionItem, AccordionTrigger, AccordionContent,
} from "../components/ui/accordion";

const WHY_ICONS = [ShieldCheck, Layers, Sparkles, Fingerprint, QrCode, Headset];
const MARQUEE = ["ANTI-COUNTERFEITING", "TAMPER-EVIDENT SEALS", "3D HOLOGRAPHY", "NANO-TEXT", "QR AUTHENTICATION", "SERIAL NUMBERING", "CUSTOM SECURITY PATTERNS"];

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);

  return (
    <>
      <Seo
        title="Hologram Sticker Manufacturer in UAE | NanoMax Holo Solutions"
        description="NanoMax Holo Solutions is a UAE hologram sticker manufacturer in Sharjah offering custom hologram stickers, security labels, QR holograms, tamper-evident labels and anti-counterfeit solutions."
        path="/"
        image={IMG.hero}
        jsonLd={[orgLd, localBusinessLd, websiteLd, faqLd(FAQS)]}
      />

      {/* HERO */}
      <section ref={heroRef} className="relative min-h-[92vh] flex items-center overflow-hidden">
        <motion.div style={{ y, scale }} className="absolute inset-0 z-0">
          <img src={IMG.hero} alt="Premium 3D holographic security sticker macro with iridescent foil" className="w-full h-full object-cover opacity-45" loading="eager" />
          <div className="absolute inset-0 bg-gradient-to-r from-bg via-bg/70 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-bg via-transparent to-bg/40" />
        </motion.div>

        <div className="relative z-10 px-6 md:px-10 lg:px-16 grid lg:grid-cols-12 gap-8 w-full">
          <div className="lg:col-span-9">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}>
              <Overline className="mb-6">Sharjah, UAE — Security Label Manufacturing</Overline>
            </motion.div>
            <h1 className="font-head font-black uppercase tracking-tighter leading-[0.92] text-4xl sm:text-6xl lg:text-7xl">
              <LineReveal lines={["Hologram Sticker", <>Manufacturer in <span className="holo-text">the UAE</span></>]} />
            </h1>
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.9 }}
              className="mt-8 max-w-2xl text-base md:text-lg text-[#8A8A9B] leading-relaxed">
              Advanced hologram &amp; security label solutions for UAE businesses. Custom hologram stickers and security labels designed to help protect products, verify authenticity and strengthen your brand.
            </motion.p>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.9 }}
              className="mt-10 flex flex-wrap items-center gap-4">
              <Link to="/contact" className="btn-primary px-8 py-4 text-sm flex items-center gap-2" data-testid="hero-quote-cta">
                Get a Free Quote <ArrowRight size={16} />
              </Link>
              <a href={SITE.whatsapp} target="_blank" rel="noreferrer" className="btn-ghost px-8 py-4 text-sm" data-testid="hero-whatsapp-cta">WhatsApp {SITE.phone}</a>
            </motion.div>
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.1 }}
              className="mt-8 font-mono2 text-[11px] uppercase tracking-[0.15em] text-[#555566]">
              Based in Sharjah, UAE · Serving businesses across the UAE
            </motion.p>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <div className="border-y border-white/10 bg-surface py-5 overflow-hidden">
        <div className="marquee-track">
          {[0, 1].map((k) => (
            <div key={k} className="flex items-center gap-16 pr-16 shrink-0">
              {MARQUEE.map((m, i) => (
                <span key={i} className="font-mono2 text-xs uppercase tracking-[0.2em] text-[#8A8A9B] flex items-center gap-16">
                  {m} <span className="text-holo-cyan">◆</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* KEY BENEFITS / PRODUCT TILES */}
      <section className="px-6 md:px-10 lg:px-16 py-24 md:py-32">
        <Reveal>
          <Overline>01 — Products</Overline>
          <h2 className="font-head text-3xl md:text-5xl font-bold tracking-tight mt-4 max-w-3xl">Hologram stickers &amp; security labels, engineered for authenticity.</h2>
        </Reveal>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-14">
          {HOME_TILES.map((t, i) => (
            <Reveal key={t.href} delay={i * 0.06}>
              <Link to={t.href} className="card grayscale-hover group block h-full overflow-hidden" data-testid={`home-tile-${i}`}>
                <div className="aspect-[4/3] overflow-hidden">
                  <img src={t.image} alt={t.alt} className="w-full h-full object-cover" loading="lazy" />
                </div>
                <div className="p-6">
                  <h3 className="font-head text-xl font-semibold tracking-tight group-hover:text-holo-cyan transition-colors">{t.title}</h3>
                  <p className="text-sm text-[#8A8A9B] mt-3 leading-relaxed">{t.desc}</p>
                  <span className="link-arrow mt-5 inline-flex">{t.cta} <span aria-hidden>&rarr;</span></span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* WHY NANOMAX — manifesto */}
      <section className="border-t border-white/10">
        <div className="px-6 md:px-10 lg:px-16 py-16">
          <Overline>02 — Why NanoMax</Overline>
        </div>
        {WHY.map((w, i) => {
          const Icon = WHY_ICONS[i];
          return (
            <Reveal key={w.t}>
              <div className="px-6 md:px-10 lg:px-16 py-10 border-t border-white/10 grid grid-cols-12 gap-6 items-start hover:bg-surface/40 transition-colors group">
                <div className="col-span-12 md:col-span-3 flex items-center gap-4">
                  <span className="font-mono2 text-3xl text-[#333340] group-hover:text-holo-cyan transition-colors">0{i + 1}</span>
                  <h3 className="font-head text-xl font-semibold tracking-tight">{w.t}</h3>
                </div>
                <p className="col-span-12 md:col-span-7 text-base md:text-lg text-[#8A8A9B] leading-relaxed">{w.d}</p>
                <div className="col-span-12 md:col-span-2 flex md:justify-end">
                  <Icon size={30} className="text-[#333340] group-hover:text-holo-cyan transition-colors" />
                </div>
              </div>
            </Reveal>
          );
        })}
      </section>

      {/* SOLUTIONS */}
      <section className="px-6 md:px-10 lg:px-16 py-24 md:py-32 border-t border-white/10">
        <Reveal>
          <Overline>03 — Security Solutions</Overline>
          <h2 className="font-head text-3xl md:text-5xl font-bold tracking-tight mt-4 max-w-3xl">Product authentication &amp; anti-counterfeit solutions.</h2>
        </Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-14">
          {SOLUTIONS.map((s, i) => (
            <Reveal key={s.slug} delay={i * 0.05}>
              <Link to={`/solutions/${s.slug}`} className="card p-6 h-full block group" data-testid={`home-solution-${i}`}>
                <h3 className="font-head text-lg font-semibold group-hover:text-holo-cyan transition-colors">{s.name}</h3>
                <p className="text-sm text-[#8A8A9B] mt-3 leading-relaxed">{s.tile}</p>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* INDUSTRIES */}
      <section className="px-6 md:px-10 lg:px-16 py-24 md:py-32 border-t border-white/10">
        <Reveal>
          <Overline>04 — Industries</Overline>
          <h2 className="font-head text-3xl md:text-5xl font-bold tracking-tight mt-4 max-w-3xl">Hologram security for UAE industries.</h2>
        </Reveal>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-px bg-white/10 mt-14 border border-white/10">
          {INDUSTRIES.map((ind, i) => {
            const Icon = INDUSTRY_ICONS[ind.slug];
            return (
              <Link key={ind.slug} to={`/industries/${ind.slug}`} className="bg-bg p-6 hover:bg-surface transition-colors group" data-testid={`home-industry-${i}`}>
                <div className="flex items-center justify-between">
                  <span className="font-mono2 text-[10px] text-[#555566]">/{String(i + 1).padStart(2, "0")}</span>
                  {Icon && <Icon size={20} className="text-[#333340] group-hover:text-holo-cyan transition-colors" />}
                </div>
                <h3 className="font-head text-base font-semibold mt-3 group-hover:text-holo-cyan transition-colors">{ind.name}</h3>
              </Link>
            );
          })}
        </div>
      </section>

      {/* CUSTOMIZATION */}
      <section className="relative border-t border-white/10 overflow-hidden">
        <img src={IMG.techLines} alt="" aria-hidden className="absolute inset-0 w-full h-full object-cover opacity-15" loading="lazy" />
        <div className="relative px-6 md:px-10 lg:px-16 py-24 md:py-32">
          <Reveal>
            <Overline>05 — Customization</Overline>
            <h2 className="font-head text-3xl md:text-5xl lg:text-6xl font-bold tracking-tight mt-4 max-w-4xl">Your brand. Your security. <span className="holo-text">Your hologram.</span></h2>
          </Reveal>
          <div className="flex flex-wrap gap-3 mt-12">
            {CONFIG_OPTIONS.map((o, i) => (
              <Reveal key={o} delay={i * 0.03}>
                <span className="card px-5 py-3 font-mono2 text-xs uppercase tracking-[0.1em] text-[#C9C9D4] hover:text-holo-cyan hover:border-holo-cyan cursor-default block">{o}</span>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="px-6 md:px-10 lg:px-16 py-24 md:py-32 border-t border-white/10">
        <Reveal>
          <Overline>06 — How It Works</Overline>
          <h2 className="font-head text-3xl md:text-5xl font-bold tracking-tight mt-4">From requirement to delivery.</h2>
        </Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-14">
          {STEPS.map((s, i) => (
            <Reveal key={s.n} delay={i * 0.05}>
              <div className="card p-8 h-full">
                <div className="font-mono2 text-4xl holo-text font-bold">{s.n}</div>
                <h3 className="font-head text-xl font-semibold mt-4">{s.t}</h3>
                <p className="text-sm text-[#8A8A9B] mt-3 leading-relaxed">{s.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal>
          <div className="mt-14 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 card p-8">
            <h3 className="font-head text-2xl md:text-3xl font-bold">Ready to protect your products?</h3>
            <Link to="/contact" className="btn-primary px-8 py-4 text-sm" data-testid="how-cta">Request a Free Quote</Link>
          </div>
        </Reveal>
      </section>

      {/* UAE SERVICE AREAS */}
      <section className="relative border-t border-white/10 overflow-hidden">
        <img src={IMG.dubai} alt="" aria-hidden className="absolute inset-0 w-full h-full object-cover opacity-20 grayscale" loading="lazy" />
        <div className="relative px-6 md:px-10 lg:px-16 py-24 md:py-32">
          <Reveal>
            <Overline>07 — UAE Service Areas</Overline>
            <h2 className="font-head text-3xl md:text-5xl font-bold tracking-tight mt-4 max-w-3xl">Serving businesses across the UAE.</h2>
            <p className="text-[#8A8A9B] mt-5 max-w-2xl">NanoMax Holo Solutions provides custom hologram sticker and security label solutions for businesses across the United Arab Emirates, with our company based in Sharjah Media City.</p>
          </Reveal>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-12">
            {LOCATIONS.map((l, i) => (
              <Reveal key={l.slug} delay={i * 0.04}>
                <Link to={`/locations/${l.slug}`} className="card p-6 block h-full group" data-testid={`home-location-${i}`}>
                  <h3 className="font-head text-lg font-semibold group-hover:text-holo-cyan transition-colors">{l.city}</h3>
                  <p className="text-xs text-[#8A8A9B] mt-2 leading-relaxed">{l.tile}</p>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* APPLICATIONS */}
      <section className="px-6 md:px-10 lg:px-16 py-24 md:py-32 border-t border-white/10">
        <Reveal>
          <Overline>08 — Applications</Overline>
          <h2 className="font-head text-3xl md:text-5xl font-bold tracking-tight mt-4">Where hologram labels are used.</h2>
        </Reveal>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6 mt-14">
          {APPLICATIONS.map((a, i) => (
            <Reveal key={a.t} delay={i * 0.03}>
              <div className="card p-6 h-full">
                <h3 className="font-head text-base font-semibold">{a.t}</h3>
                <p className="text-xs text-[#8A8A9B] mt-2 leading-relaxed">{a.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="px-6 md:px-10 lg:px-16 py-24 md:py-32 border-t border-white/10 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-4">
          <Overline>09 — FAQ</Overline>
          <h2 className="font-head text-3xl md:text-4xl font-bold tracking-tight mt-4">Frequently asked questions.</h2>
        </div>
        <div className="lg:col-span-8">
          <Accordion type="single" collapsible className="w-full" data-testid="home-faq">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`f${i}`} className="border-white/10">
                <AccordionTrigger className="text-left font-head text-base md:text-lg hover:text-holo-cyan hover:no-underline">{f.q}</AccordionTrigger>
                <AccordionContent className="text-[#8A8A9B] text-sm leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* FINAL QUOTE CTA */}
      <section className="relative border-t border-white/10 overflow-hidden">
        <img src={IMG.ctaBg} alt="" aria-hidden className="absolute inset-0 w-full h-full object-cover opacity-30" loading="lazy" />
        <div className="relative px-6 md:px-10 lg:px-16 py-24 md:py-32 grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <Overline>Get a Quote</Overline>
            <h2 className="font-head text-3xl md:text-5xl font-bold tracking-tight mt-4">Request a free hologram sticker quote.</h2>
            <p className="text-[#8A8A9B] mt-5 max-w-md">Tell us about your product and security requirements. Our UAE-based team will review and get back to you shortly.</p>
            <div className="mt-8 flex flex-col gap-2 font-mono2 text-sm">
              <a href={SITE.whatsapp} target="_blank" rel="noreferrer" className="link-arrow" data-testid="cta-whatsapp">Chat with NanoMax on WhatsApp <span>&rarr;</span></a>
              <a href={`tel:${SITE.phoneRaw}`} className="link-arrow" data-testid="cta-phone">Call {SITE.phone} <span>&rarr;</span></a>
            </div>
          </Reveal>
          <Reveal delay={0.15}><QuoteForm sourcePage="/" /></Reveal>
        </div>
      </section>
    </>
  );
}
